import { Button, Buttons, Content, Header, Modal, Title, Toolbar, isPlainClick } from '@/ui'
import { useAdminInboxStore } from '@/stores/adminInbox'
import { useAuthStore } from '@/stores/auth'
import { useTrainingStore } from '@/stores/training'
import { useTournamentsStore } from '@/stores/tournaments'
import './AdminActionInboxModal.css'

export interface AdminActionInboxModalProps {
  isOpen?: boolean
  onDismiss?: () => void
  onLater?: () => void
}

const uniqueParents = (rows: any[], key: string) => {
  const seen = new Set()
  const out: any[] = []
  for (const row of rows || []) {
    const id = row[key]
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(row)
  }
  return out
}

export function AdminActionInboxModal ({ isOpen = false, onDismiss, onLater }: AdminActionInboxModalProps) {
  const inbox = useAdminInboxStore()
  const adminCityIds = useAuthStore((s) => s.adminCityIds)
  const trainingList = useTrainingStore((s) => s.list)
  const tournamentsList = useTournamentsStore((s) => s.list)

  const counts = inbox.counts

  const parentLabel = (id: string, kind: 'tournament' | 'training') => {
    if (kind === 'training') {
      const s = trainingList.find((x) => x.id === id)
      return s?.name || 'clinic'
    }
    const t = tournamentsList.find((x) => x.id === id)
    return t?.name || 'event'
  }

  const firstAdminCity = () => {
    if (adminCityIds?.length) return adminCityIds[0]
    return 'halifax'
  }

  // Links close the inbox on a plain click; a new-tab click leaves it open.
  const closeOnNavigate = (e: React.MouseEvent) => {
    if (isPlainClick(e)) onDismiss?.()
  }

  const skillPath = (row: any) => `/${firstAdminCity()}/admin/edit-user/${row.userId || row.id}`

  return (
    <Modal isOpen={isOpen} onDidDismiss={() => onDismiss?.()} className="c-admin-action-inbox-modal" ariaLabel="Review queue">
      <Header>
        <Toolbar>
          <Title>Review queue</Title>
          <Buttons slot="end">
            <Button fill="clear" onClick={() => onDismiss?.()}>Close</Button>
          </Buttons>
        </Toolbar>
      </Header>
      <Content padding>
        <p className="lead">
          You have:{' '}
          <strong>{counts.pickup}</strong> pickup regular request{counts.pickup === 1 ? '' : 's'}
          {' · '}
          <strong>{counts.tournament}</strong> tournament application{counts.tournament === 1 ? '' : 's'}
          {' · '}
          <strong>{counts.training}</strong> training application{counts.training === 1 ? '' : 's'}
          {' · '}
          <strong>{counts.skills}</strong> skills rating request{counts.skills === 1 ? '' : 's'}
        </p>

        {inbox.loading && <p className="muted">Refreshing…</p>}

        {counts.pickup ? (
          <section className="queue">
            <h3>Pickup regulars</h3>
            <Button expand="block" routerLink={`/${firstAdminCity()}/admin`} onClick={closeOnNavigate}>Open city admin</Button>
          </section>
        ) : null}

        {counts.tournament ? (
          <section className="queue">
            <h3>Tournament applications</h3>
            {uniqueParents(inbox.tournamentPending, 'parentId').map((row) => (
              <Button
                key={'t-' + row.parentId}
                expand="block"
                fill="outline"
                routerLink={`/tournaments/${row.parentId}/applications`}
                onClick={closeOnNavigate}
              >
                Review {parentLabel(row.parentId, 'tournament')}
              </Button>
            ))}
          </section>
        ) : null}

        {counts.training ? (
          <section className="queue">
            <h3>Training applications</h3>
            {uniqueParents(inbox.trainingPending, 'parentId').map((row) => (
              <Button
                key={'tr-' + row.parentId}
                expand="block"
                fill="outline"
                routerLink={`/training/${row.parentId}/applications`}
                onClick={closeOnNavigate}
              >
                Review {parentLabel(row.parentId, 'training')}
              </Button>
            ))}
          </section>
        ) : null}

        {counts.skills ? (
          <section className="queue">
            <h3>Skills rating requests</h3>
            {inbox.skillsPending.map((row) => (
              <Button
                key={'s-' + row.id}
                expand="block"
                fill="outline"
                routerLink={skillPath(row)}
                onClick={closeOnNavigate}
              >
                {row.displayName || row.id}
              </Button>
            ))}
          </section>
        ) : null}

        <Button expand="block" className="later" onClick={() => onLater?.()}>
          Review later
        </Button>
      </Content>
    </Modal>
  )
}
